import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserData } from "../../types/user-data.type";
import useApi from "../../hooks/api/useApi";

const AVAILABLE_ROLES = ["Admin", "Member"];
export default function AdminUserForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { request } = useApi();

    const userData: UserData = JSON.parse(localStorage.getItem("user") ?? "");
    const authHeaders = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.authToken,
    };

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        username: "",
        password: "",
        active: true,
        roles: [] as string[],
        groupId: null as string | null,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEdit);
    const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
    const [groupSearch, setGroupSearch] = useState("");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchGroups = (q: string) => {
        request(
            `/groups?search=${q}&page=1`,
            { method: "Get", headers: authHeaders },
            (result) => setGroups(result.payload.items)
        );
    };

    useEffect(() => {
        fetchGroups("");
    }, []);

    const handleGroupSearch = (q: string) => {
        setGroupSearch(q);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchGroups(q), 300);
    };
    useEffect(() => {
        if (!isEdit) return;
        request(
            `/users/${id}`,
            { method: "Get", headers: authHeaders },
            (result) => {
                const u = result.payload;
                setForm({
                    firstName: u.firstName,
                    lastName: u.lastName,
                    username: u.username,
                    password: "",
                    active: u.active,
                    roles: u.roles,
                    groupId: u.groupId ?? null,

                });
                setInitialLoading(false);
            }
        );
    }, [id]);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.firstName.trim()) e.firstName = "نام اجباری است";
        if (!form.lastName.trim()) e.lastName = "نام خانوادگی اجباری است";
        if (!form.username.trim()) e.username = "نام کاربری اجباری است";
        if (!isEdit && !form.password.trim()) e.password = "رمز عبور اجباری است";
        return e;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setLoading(true);
        const payload: Record<string, unknown> = {
            firstName: form.firstName,
            lastName: form.lastName,
            username: form.username,
            active: form.active,
            roles: form.roles,
            groupId: form.groupId,
        };
        if (form.password) payload.password = form.password;

        try {
            await request(
                isEdit ? `/users/${id}` : "/users",
                { method: isEdit ? "Put" : "Post", headers: authHeaders, body: JSON.stringify(payload) },
                () => navigate("/admin/users")
            );
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleRole = (role: string) => {
        setForm((prev) => ({
            ...prev,
            roles: prev.roles.includes(role)
                ? prev.roles.filter((r) => r !== role)
                : [...prev.roles, role],
        }));
    };

    if (initialLoading) return <div className="p-6 text-gray-400">در حال بارگذاری...</div>;

    return (
        <div className="min-h-screen text-gray-800 font-sans">
            <div className="mx-auto max-w-lg p-6 space-y-6">
                <h1 className="text-2xl font-semibold text-gray-800">
                    {isEdit ? "ویرایش کاربر" : "ایجاد کاربر جدید"}
                </h1>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* نام */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">نام</label>
                        <input
                            type="text"
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>

                    {/* نام خانوادگی */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
                        <input
                            type="text"
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>

                    {/* ایمیل */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری</label>
                        <input
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                    </div>

                    {/* رمز عبور */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            رمز عبور {isEdit && <span className="text-gray-400 font-normal">(خالی = بدون تغییر)</span>}
                        </label>
                        <input
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    </div>

                    {/* نقش‌ها */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نقش‌ها</label>
                        <div className="flex gap-2 flex-wrap">
                            {AVAILABLE_ROLES.map((role) => (
                                <button
                                    key={role}
                                    type="button"
                                    onClick={() => toggleRole(role)}
                                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.roles.includes(role)
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                                        }`}
                                >
                                    {role}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* گروه */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            گروه <span className="text-gray-400 font-normal">(اختیاری)</span>
                        </label>
                        <input
                            type="text"
                            value={groupSearch}
                            onChange={(e) => handleGroupSearch(e.target.value)}
                            placeholder="جستجوی گروه..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                        />
                        <div className="flex flex-wrap gap-2"><button
                            type="button"
                            onClick={() => setForm({ ...form, groupId: null })}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.groupId === null
                                    ? "bg-gray-700 text-white border-gray-700"
                                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                }`}
                        >
                            بدون گروه
                        </button>
                            {groups.map((g) => (
                                <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => setForm({ ...form, groupId: g.id })}
                                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.groupId === g.id
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                                        }`}
                                >
                                    {g.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* وضعیت فعال */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="active"
                            checked={form.active}
                            onChange={(e) => setForm({ ...form, active: e.target.checked })}
                            className="w-4 h-4 accent-blue-600"
                        />
                        <label htmlFor="active" className="text-sm text-gray-700">کاربر فعال باشد</label>
                    </div>

                    {/* دکمه‌ها */}
                    <div className="flex gap-2 pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            {loading ? "در حال ذخیره..." : "ذخیره"}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/admin/users")}
                            className="px-5 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            انصراف
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
